import * as React from "react";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/Common/Spinner";
import { cva, VariantProps } from "class-variance-authority";
import { Checkbox } from "./Checkbox";
import { ReactNode } from "react";
import { comboboxMatches } from "@/lib/comboboxSearch";
import { Popover, PopoverAnchor, PopoverContent } from "./Popover";

// Types
type Option = {
  value: string;
  label?: string;
};

type MultiSelectProps = {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  /** Extra classes merged onto the trigger button (className goes to the wrapper). */
  triggerClassName?: string;
  variant?: "default" | "ghost" | "minimal";
  itemsName?: string;
  renderItem?: (option: Option, isSelected: boolean) => ReactNode;
  /**
   * Shows a text input above the option list that filters it via
   * comboboxMatches. Off by default so existing consumers (task/dashboard
   * filters, automation builder, etc.) render unchanged — opt in per call
   * site where the option list is long enough to need filtering (e.g. the
   * Layout Builder's container picker). Filtering matches against
   * `option.label ?? option.value`, so a `renderItem` that displays
   * different visible text should keep that text reflected in `option.label`
   * or search will filter on text the user can't see.
   */
  searchable?: boolean;
  /**
   * Renders the option list in a portal (Radix Popover) instead of inline. The
   * inline list is `position: absolute` inside the component's own wrapper, so
   * an `overflow` ancestor (a scrolling dialog body, say) clips it. Off by
   * default so existing consumers render exactly as before; opt in where the
   * picker sits inside a scroll container.
   */
  portal?: boolean;
};

// Trigger styles (borrowed from Select component)
const triggerVariants = cva(
  "flex h-10 w-full items-center justify-between bg-background text-primary px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 data-[placeholder]:text-muted",
  {
    variants: {
      variant: {
        default: "rounded-md border border-input",
        ghost: "",
        minimal: "border-b border-input",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const MultiSelect = React.forwardRef<
  HTMLDivElement,
  MultiSelectProps & VariantProps<typeof triggerVariants>
>(
  (
    {
      options,
      value,
      onChange,
      placeholder = "Select options",
      itemsName = "items",
      disabled = false,
      loading = false,
      className,
      triggerClassName,
      variant,
      renderItem,
      searchable = false,
      portal = false,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    // Portal mode only: whether the current close came from the user
    // interacting elsewhere, in which case focus must stay where they put it.
    const closedByOutsideInteractionRef = React.useRef(false);
    const [search, setSearch] = React.useState("");
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Reset the filter text whenever the dropdown closes (trigger click or
    // backdrop click — selecting an option deliberately leaves the dropdown
    // open for multi-select), so reopening always starts from the full list.
    React.useEffect(() => {
      if (!open) {
        setSearch("");
      }
    }, [open]);

    React.useEffect(() => {
      if (open && searchable) {
        searchInputRef.current?.focus();
      }
    }, [open, searchable]);

    const filteredOptions = React.useMemo(
      () =>
        searchable
          ? options.filter((option) =>
              comboboxMatches(option.label ?? option.value, search)
            )
          : options,
      [options, search, searchable]
    );

    // Handle toggle of checkbox selection
    const handleSelect = (optionValue: string) => {
      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];

      onChange(newValue);
    };

    // Display text for selected values
    const displayValue = React.useMemo(() => {
      if (value.length === 0) return placeholder;

      if (value.length <= 2) {
        return value
          .map((v) => options.find((option) => option.value === v)?.label)
          .join(", ");
      }

      return `${value.length} ${itemsName} selected`;
    }, [value, options, placeholder]);

    const triggerButton = (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && !loading && setOpen(!open)}
        className={cn(
          triggerVariants({ variant }),
          triggerClassName,
          // When a selection is active: white bg, xs shadow, primary-blue text
          // (the blue used by the + button). Placed last so it wins over the
          // pill/ghost trigger classes.
          value.length > 0 && "bg-white text-primary shadow-sm"
        )}
        disabled={disabled || loading}
        aria-expanded={open}
      >
        {loading ? (
          <div className="mr-2 flex">
            <Spinner size="sm" />
          </div>
        ) : null}
        <span className="flex-grow text-left">{displayValue}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-current" />
      </button>
    );

    // Search box + option list, shared by the inline and portalled dropdowns.
    const menuBody = (
      <>
        {searchable && (
          <div className="mb-1 flex items-center border-b border-input px-2">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search..."
              className="h-8 w-full bg-transparent text-sm text-primary outline-none placeholder:text-muted"
            />
          </div>
        )}
        <div className="max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No options available
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No matches
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = value.includes(option.value);

              return (
                <div
                  key={option.value}
                  className="flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(option.value);
                  }}
                >
                  <Checkbox
                    checked={value.includes(option.value)}
                    // border-primary: the shared Checkbox border is
                    // near-white and invisible on the light popover.
                    className="mr-2 border-primary"
                    onCheckedChange={() => handleSelect(option.value)}
                  />
                  {renderItem ? (
                    renderItem(option, isSelected)
                  ) : (
                    <span>{option.label}</span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </>
    );

    if (portal) {
      return (
        <div ref={ref} className={className} {...props}>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>{triggerButton}</PopoverAnchor>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] min-w-40 overflow-hidden p-1"
              // The trigger is an anchor here, not a Radix Trigger, so a
              // pointer down on it reads as an "outside" interaction: the
              // list would close on pointer down and the button's own onClick
              // would toggle it straight back open.
              onInteractOutside={(event) => {
                if (triggerRef.current?.contains(event.target as Node)) {
                  event.preventDefault();
                  return;
                }
                closedByOutsideInteractionRef.current = true;
              }}
              // Non-searchable: keep focus on the trigger. Searchable: let
              // Radix move focus to the first tabbable, the search input. (The
              // inline path's focus effect above can't do this here: it runs
              // before Radix mounts the portalled content, so the ref is
              // still null.)
              onOpenAutoFocus={(event) => {
                closedByOutsideInteractionRef.current = false;
                if (!searchable) event.preventDefault();
              }}
              // The anchor isn't a Radix Trigger, so Radix's own "return focus
              // to the trigger on close" is a no-op; do it ourselves so Escape
              // from the search input doesn't drop focus to <body>. Like
              // Radix, skip it when the user closed the list by interacting
              // elsewhere, so we don't steal focus from what they clicked.
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                if (!closedByOutsideInteractionRef.current) {
                  triggerRef.current?.focus();
                }
              }}
            >
              {menuBody}
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    return (
      <div ref={ref} className={className} {...props}>
        {triggerButton}

        {/* Dropdown menu */}
        {open && (
          <div className="relative z-50">
            <div className="fixed inset-0" onClick={() => setOpen(false)} />
            <div className="absolute z-50 mt-1 w-full min-w-40 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
              {menuBody}
            </div>
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
